import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Home as HomeIcon } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, pw);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:block relative overflow-hidden bg-[var(--app-black)] text-white">
        <div className="absolute inset-0 grain" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <div className="flex items-center gap-2.5">
            <span className="inline-grid place-items-center w-10 h-10 rounded-full bg-white text-[var(--app-black)]">
              <HomeIcon size={18} strokeWidth={2.4} />
            </span>
            <div className="font-display text-xl">FlipPilot</div>
          </div>
          <div>
            <h1 className="font-display text-5xl lg:text-6xl leading-[0.95]">
              Close the deal
              <br />
              <span className="text-white/60">before your competitor</span>
              <br />
              <span style={{ color: "var(--app-accent-2)" }}>even sees it.</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              AI-powered acquisition platform for wholesalers, flippers, and
              buy-and-hold investors. From address to signed offer in under a
              minute.
            </p>
          </div>
          <div className="text-xs text-white/50">
            © 2026 FlipPilot · Not a substitute for legal counsel
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form
          onSubmit={submit}
          data-testid="login-form"
          className="w-full max-w-sm space-y-6"
        >
          <div>
            <span className="app-eyebrow">/ Welcome back</span>
            <h2 className="font-display text-4xl mt-2">Sign in</h2>
            <p className="text-[var(--app-text-muted)] text-sm mt-2">
              Analyze your next deal in under a minute.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field-sharp h-11"
              data-testid="login-email"
              placeholder="you@company.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw">Password</Label>
            <Input
              id="pw"
              type="password"
              required
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="field-sharp h-11"
              data-testid="login-password"
              placeholder="••••••••"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit"
            className="app-btn app-btn-primary w-full h-11"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-sm text-[var(--app-text-muted)] text-center">
            Need access?{" "}
            <Link
              to="/request-access"
              className="text-[var(--app-accent-dark)] hover:text-[var(--app-accent)] font-medium"
              data-testid="link-request-access"
            >
              Request Approval · Waiting List
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
